import { ColumnDef, Row } from '@tanstack/react-table';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Disclosure, DisclosurePanel } from 'react-aria-components';

import { Button, DebouncedInput, Flex, Heading, Table, TableProps, Text, TextField } from '@/components/core';
import { AddIcon, ChevronIcon } from '@/components/icons';
import { useTriggerStore } from '@/store';
import { generateId, jsonToList, toString } from '@/utils';

type HttpHeader = {
  id: string;
  key?: string;
  value?: string;
};

type IHttpHeaderProps = Omit<TableProps<HttpHeader>, 'columns' | 'data'>;

const IHttpHeaders = ({ ...rest }: IHttpHeaderProps) => {
  const { getAction, updateAction } = useTriggerStore((state) => state);
  const action = useTriggerStore((state) => state.action);

  const headers = useMemo(() => jsonToList<HttpHeader>(action.config?.headers), [action.config]);

  useEffect(() => {
    updateAction((action) => ({ config: { ...action.config, headers } }));
  }, [headers, updateAction]);

  const updateHeader = useCallback(
    (id: string, prop: keyof HttpHeader, val?: string) => {
      updateAction((action) => {
        const headers = jsonToList<HttpHeader>(action.config?.headers);
        const header = headers.find((h: HttpHeader) => h.id === id);
        if (header) header[prop] = toString(val);

        const oldConfig = action?.config;
        return { ...action, config: { ...oldConfig, headers } };
      });
    },
    [updateAction]
  );

  const onDeleteRow = useCallback(
    (row: Row<HttpHeader>) => {
      updateAction((action) => {
        const headers = jsonToList<HttpHeader>(action.config?.headers).filter((h: HttpHeader) => h.id !== row.id);
        return { ...action, config: { ...action?.config, headers } };
      });
    },
    [updateAction]
  );

  const onAddRow = useCallback(() => {
    updateAction((action) => {
      const headers = jsonToList<HttpHeader>(action.config?.headers);
      const oldConfig = action?.config;
      return { ...action, config: { ...oldConfig, headers: [...headers, { id: generateId() }] } };
    });
  }, [updateAction]);

  const columns = useMemo<ColumnDef<HttpHeader>[]>(() => {
    const headers = jsonToList<HttpHeader>(getAction().config?.headers);

    return [
      {
        accessorKey: 'key',
        cell: ({ row }) => (
          <TextField defaultValue={headers[row.index]?.key}>
            <DebouncedInput onChange={(e) => updateHeader(row.id, 'key', e.target.value)} placeholder="Key" />
          </TextField>
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
      {
        accessorKey: 'value',
        cell: ({ row }) => (
          <TextField defaultValue={headers[row.index]?.value}>
            <DebouncedInput onChange={(e) => updateHeader(row.id, 'value', e.target.value)} placeholder="Value" />
          </TextField>
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, [getAction, updateHeader]);

  return (
    <Flex col>
      <Disclosure className="group/http-headers">
        <Button slot="trigger" className="items-center bg-transparent p-0">
          <ChevronIcon rotation="down" className="-rotate-90 group-data-[expanded]/http-headers:rotate-0" />
          <Heading size="xs">HTTP Headers</Heading>
        </Button>
        <DisclosurePanel>
          <Flex col className="gap-sm mr-lg items-end">
            <Table
              columns={columns}
              data={headers}
              deleteRowAction={onDeleteRow}
              {...rest}
              hideHeader
              draggable={false}
            />
            <Button onPress={onAddRow} variant="plain">
              <AddIcon />
              <Text>Add header</Text>
            </Button>
          </Flex>
        </DisclosurePanel>
      </Disclosure>
    </Flex>
  );
};

export const HttpHeaders = memo(IHttpHeaders) as typeof IHttpHeaders;
